import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { WarehouseProvider } from './contexts/WarehouseContext';
import { LocationProvider } from './contexts/LocationContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import { BulkApprovalProvider } from './contexts/BulkApprovalContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/layout/Layout';
import PosPage from './pages/PosPage';
import KasaBankPage from './pages/KasaBankPage';
import LocationsPage from './pages/LocationsPage';
import LocationPricingPage from './pages/LocationPricingPage';
import ProductsPage from './pages/ProductsPage';
import CustomersPage from './pages/CustomersPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import CouponsPage from './pages/CouponsPageSimple';
import InvoicesPage from './pages/InvoicesPage';
import SalesInvoicesPage from './pages/SalesInvoicesPage';
import PurchaseInvoicesPage from './pages/PurchaseInvoicesPage';
import PurchaseInvoiceWizardPage from './pages/PurchaseInvoiceWizardPage';
import PurchaseInvoicesListPage from './pages/PurchaseInvoicesListPage';
import PurchaseInvoiceDetailsPage from './pages/PurchaseInvoiceDetailsPage';
import PurchaseInvoiceEditPage from './pages/PurchaseInvoiceEditPage';
import PurchaseReportsPage from './pages/PurchaseReportsPage';
import CennikHistoryPage from './pages/CennikHistoryPage';
import WarehousePage from './pages/WarehousePage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import CategoriesAdmin from './pages/CategoriesAdmin';
import OrdersPage from './pages/OrdersPage';
import DocumentPrefixesPage from './pages/DocumentPrefixesPage';
import ReturnPrintPage from './pages/ReturnPrintPage';
import ReceiptPrintPage from './pages/ReceiptPrintPage';
import CorrectionPrintPage from './pages/CorrectionPrintPage';
import ShippingSettingsPage from './pages/ShippingSettingsPage';
import ShipmentsPage from './pages/ShipmentsPage';
import ShippingPage from './pages/ShippingPage';
import TasksPage from './pages/TasksPage';
import PomocPage from './pages/PomocPage';
import MessagesPage from './pages/MessagesPage';
import AccountingPage from './pages/AccountingPage';
import ExternalCostsPage from './pages/ExternalCostsPage';
import ProfitabilityPage from './pages/ProfitabilityPage';
import ThemeSettingsPage from './pages/ThemeSettingsPage';
import KolektorPage from './pages/KolektorPage';
import BonusPage from './pages/BonusPage';
import MyBonusPage from './pages/MyBonusPage';
import MySchedulePage from './pages/MySchedulePage';
import ReklamacjePage from './pages/ReklamacjePage';
import ReklamacjaFormPage from './pages/ReklamacjaFormPage';
import ReklamacjaPrintPage from './pages/ReklamacjaPrintPage';
import KomunikatorWidget from './components/komunikator/KomunikatorWidget';

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <ThemeProvider>
          <LocationProvider>
            <WarehouseProvider>
              <BulkApprovalProvider>
              <div className="app">
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                {/* Publiczna strona logowania - bez Header */}
                <Route path="/login" element={<LoginPage />} />
                
                {/* Chronione trasy - z Header w Layout */}
                <Route path="/" element={<Navigate to="/pos" replace />} />
                <Route path="/pos" element={<ProtectedRoute><Layout><CartProvider><PosPage /></CartProvider></Layout></ProtectedRoute>} />
                <Route path="/products" element={<ProtectedRoute><Layout><ProductsPage /></Layout></ProtectedRoute>} />
                <Route path="/customers" element={<ProtectedRoute><Layout><CustomersPage /></Layout></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><Layout><OrdersPage /></Layout></ProtectedRoute>} />
                <Route path="/shipping" element={<ProtectedRoute><Layout><ShippingPage /></Layout></ProtectedRoute>} />
                <Route path="/tasks" element={<ProtectedRoute><Layout><TasksPage /></Layout></ProtectedRoute>} />
                {/* Instrukcje dla pracownikow - bez uprawnien, dostepne dla kazdego zalogowanego */}
                <Route path="/pomoc" element={<ProtectedRoute><Layout><PomocPage /></Layout></ProtectedRoute>} />
                <Route path="/wiadomosci" element={<ProtectedRoute><Layout><MessagesPage /></Layout></ProtectedRoute>} />
                <Route path="/coupons" element={<ProtectedRoute><Layout><CouponsPage /></Layout></ProtectedRoute>} />
                <Route path="/invoices" element={<ProtectedRoute><Layout><InvoicesPage /></Layout></ProtectedRoute>} />
                <Route path="/sales-invoices" element={<ProtectedRoute><Layout><SalesInvoicesPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseInvoicesPage /></Layout></ProtectedRoute>} />
                {/* UWAGA: trasa kreatora MUSI byc przed /purchase-invoices/:id, inaczej segment 'kreator' wpadnie do widoku szczegolow jako id */}
                <Route path="/purchase-invoices/kreator" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseInvoiceWizardPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices/list" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseInvoicesListPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices/cennik-history" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><CennikHistoryPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices/reports" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseReportsPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices/new" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseInvoiceEditPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices/:id/edit" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseInvoiceEditPage /></Layout></ProtectedRoute>} />
                <Route path="/purchase-invoices/:id" element={<ProtectedRoute requiredModule="faktury_zakupu"><Layout><PurchaseInvoiceDetailsPage /></Layout></ProtectedRoute>} />
                <Route path="/kontrola-faktur" element={<Navigate to="/admin?tab=kontrola-faktur" replace />} />
                <Route path="/kontrola-korekt" element={<Navigate to="/admin?tab=kontrola-korekt" replace />} />
                <Route path="/reklamacje" element={<ProtectedRoute><Layout><ReklamacjePage /></Layout></ProtectedRoute>} />
                <Route path="/reklamacje/nowa" element={<ProtectedRoute><Layout><ReklamacjaFormPage /></Layout></ProtectedRoute>} />
                <Route path="/reklamacje/:id/edytuj" element={<ProtectedRoute><Layout><ReklamacjaFormPage /></Layout></ProtectedRoute>} />
                <Route path="/reklamacje/:id/pdf-podglad" element={<ProtectedRoute><Layout><ReklamacjaPrintPage /></Layout></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><Layout><ReportsPage /></Layout></ProtectedRoute>} />
                <Route path="/warehouse" element={<ProtectedRoute><Layout><WarehousePage /></Layout></ProtectedRoute>} />
                <Route path="/kolektor" element={<ProtectedRoute><Layout><KolektorPage /></Layout></ProtectedRoute>} />
                <Route path="/kasa-bank" element={<ProtectedRoute><Layout><KasaBankPage /></Layout></ProtectedRoute>} />
                <Route path="/accounting" element={<ProtectedRoute><Layout><AccountingPage /></Layout></ProtectedRoute>} />
                <Route path="/bonuses" element={<ProtectedRoute><Layout><BonusPage /></Layout></ProtectedRoute>} />
                <Route path="/bonuses/my" element={<ProtectedRoute><Layout><MyBonusPage /></Layout></ProtectedRoute>} />
                <Route path="/grafik" element={<ProtectedRoute><Layout><MySchedulePage /></Layout></ProtectedRoute>} />
                <Route path="/external-costs" element={<ProtectedRoute><Layout><ExternalCostsPage /></Layout></ProtectedRoute>} />
                <Route path="/profitability" element={<ProtectedRoute><Layout><ProfitabilityPage /></Layout></ProtectedRoute>} />
                <Route path="/locations" element={<ProtectedRoute><Layout><LocationsPage /></Layout></ProtectedRoute>} />
                <Route path="/location-pricing" element={<ProtectedRoute><Layout><LocationPricingPage /></Layout></ProtectedRoute>} />
                <Route path="/cenowki" element={<ProtectedRoute><Layout><LocationPricingPage /></Layout></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><Layout><AdminPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/categories" element={<ProtectedRoute requireAdmin={true}><Layout><CategoriesAdmin /></Layout></ProtectedRoute>} />
                <Route path="/admin/document-prefixes" element={<ProtectedRoute requireAdmin={true}><Layout><DocumentPrefixesPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/shipping" element={<ProtectedRoute requireAdmin={true}><Layout><ShippingSettingsPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/shipments" element={<ProtectedRoute requireAdmin={true}><Layout><ShipmentsPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/theme" element={<ProtectedRoute requireAdmin={true}><Layout><ThemeSettingsPage /></Layout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
                
                {/* Strona wydruku zwrotu - bez layoutu */}
                <Route path="/pos/return-print/:transactionId" element={<ReturnPrintPage />} />
                
                {/* Strona wydruku paragonu - bez layoutu */}
                <Route path="/pos/receipt/:transactionId" element={<ReceiptPrintPage />} />

                {/* Strona wydruku korekty paragonu - bez layoutu */}
                <Route path="/pos/correction-print/:returnId" element={<CorrectionPrintPage />} />

              </Routes>

              {/* Komunikator - globalny widget czatu.
                  Stoi POZA <Routes>, wiec przezywa nawigacje miedzy stronami i
                  nie gubi otwartej rozmowy (ten sam wzorzec co BulkApprovalBanner
                  w Layout.jsx). Wewnatrz <Router>, bo tu sa juz wszystkie
                  konteksty - sam widget hookow routera NIE uzywa, sciezke czyta
                  z window.location.
                  Nie renderuje sie, dopoki useAuth().isAuthenticated nie jest
                  true, wiec na /login go nie ma; dodatkowo sam chowa sie na
                  /kolektor i na stronach wydruku (TRASY_UKRYTE w widgecie). */}
              <KomunikatorWidget />
              </Router>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#333',
                    color: '#fff',
                  },
                  success: { duration: 3000 },
                  error: { duration: 5000 },
                }}
              />
            </div>
              </BulkApprovalProvider>
            </WarehouseProvider>
          </LocationProvider>
        </ThemeProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
